import * as p from '@clack/prompts'
import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import type { LoadedSchema } from '@cli-types/core-engine/schema-reader.js'
import type { RenamePlan } from '@cli-types/core-engine/snapshots.js'
import { buildCustomCellRenameSelection } from '@core-engine/snapshots/ast-substitution/build-custom-cell-rename-selection.js'
import { formatRenameSelectionSummary } from '@core-engine/snapshots/ast-substitution/format-rename-selection-summary.js'
import { detectRenameCandidates } from '@core-engine/snapshots/rename-detector/detect-rename-candidates.js'
import { detectStringRenameCandidates } from '@core-engine/snapshots/rename-detector/detect-string-rename-candidates.js'
import { diffSchemas } from '@core-engine/snapshots/schema-diff/diff-schemas.js'
import { loadManifest } from '@core-engine/snapshots/store/load-manifest.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'
import { collectRenamePreview } from './collect-rename-preview.js'
import { isStoredSchemaJson } from './is-stored-schema-json.js'
import { promptConfirm } from './prompt-confirm.js'
import { validateCustomCellRenameSelections } from './validate-custom-cell-rename-selections.js'

export async function maybeBuildRenamePlan(
  loaded: LoadedSchema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  generatedFiles: Array<{ path: string; content: string }>,
  options: { force: boolean; interactive: boolean }
): Promise<RenamePlan | undefined> {
  if (!options.interactive || options.force) {
    return undefined
  }

  const manifest = loadManifest(cwd, loaded.schema.name)
  if (!manifest || !isStoredSchemaJson(manifest.schemaJson)) {
    return undefined
  }

  const diff = diffSchemas(manifest.schemaJson, loaded.schema)
  const fieldCandidates = detectRenameCandidates(diff.removed, diff.added)
  const customCellCandidates = detectStringRenameCandidates(
    diff.customComponents.removed,
    diff.customComponents.added
  )

  if (fieldCandidates.length === 0 && customCellCandidates.length === 0) {
    return undefined
  }

  const plan: RenamePlan = {
    fields: [],
    customCells: []
  }

  for (const candidate of fieldCandidates) {
    const accepted = await promptConfirm(
      `Field '${candidate.before}' was removed and '${candidate.after}' was added. Treat it as a rename before merge?`
    )

    if (accepted) {
      plan.fields.push(candidate)
    }
  }

  if (customCellCandidates.length > 0) {
    const paths = resolveProjectPaths(config)
    for (const candidate of customCellCandidates) {
      const accepted = await promptConfirm(
        `Custom cell '${candidate.before}' was removed and '${candidate.after}' was added. Treat it as a rename before merge?`
      )

      if (accepted) {
        plan.customCells.push(
          buildCustomCellRenameSelection(
            loaded.schema.name,
            paths.pagesDir,
            candidate.before,
            candidate.after
          )
        )
      }
    }
  }

  const validatedCustomCells = validateCustomCellRenameSelections(
    cwd,
    plan.customCells,
    plan.fields
  )
  const finalPlan: RenamePlan = {
    fields: plan.fields,
    customCells: validatedCustomCells.renames
  }

  if (finalPlan.fields.length === 0 && finalPlan.customCells.length === 0) {
    if (validatedCustomCells.warnings.length > 0) {
      p.log.warn(validatedCustomCells.warnings.join('\n'))
    }
    return undefined
  }

  const preview = collectRenamePreview(
    cwd,
    generatedFiles.map((file) => file.path),
    finalPlan
  )

  const renameLines = [
    'Proposed rename substitutions:',
    ...formatRenameSelectionSummary(finalPlan).map((line) => `  ${line}`)
  ]

  const previewLines = preview.previewLines.slice(0, 20)
  renameLines.push(...previewLines)

  if (preview.previewLines.length > previewLines.length) {
    renameLines.push(`  ... ${preview.previewLines.length - previewLines.length} more change(s)`)
  }
  p.log.message(renameLines.join('\n'))

  const warnings = [...validatedCustomCells.warnings, ...preview.warnings]
  if (warnings.length > 0) {
    p.log.warn(warnings.join('\n'))
  }

  const confirmed = await promptConfirm('Apply these rename substitutions before merge?')
  return confirmed ? finalPlan : undefined
}
